import { useParams } from 'react-router-dom';
import { Box, Alert, Typography, CircularProgress } from '@mui/material';
import { useGetScoringBatch } from './hooks/useGetScoringBatch';
import { useRetryScoringBatch } from './hooks/useRetryScoringBatch';
import { ScoringBatchStatusCard } from './components/ScoringBatchStatusCard';
import { ScoringResultsTable } from './components/ScoringResultsTable';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';

export const ScoringBatchDetailModule = () => {
    const { batchId } = useParams<{ batchId: string }>();
    const { batch, loading, error, refetch, startPolling } = useGetScoringBatch(batchId ?? null);
    const { retryScoringBatch, loading: retryLoading } = useRetryScoringBatch();
    const { showSnackbar } = useSnackbar();

    const handleRetry = async () => {
        if (!batchId) return;
        const result = await retryScoringBatch(batchId);
        if (result && result.totalQueued > 0) {
            showSnackbar(`${result.totalQueued} résultat(s) relancé(s)`, 'info');
            startPolling(3000);
            refetch();
        } else {
            showSnackbar('Aucun résultat à relancer', 'warning');
        }
    };

    if (!batchId) {
        return <Alert severity="error">Identifiant de batch manquant.</Alert>;
    }

    if (loading && !batch) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">Erreur lors du chargement du batch : {error.message}</Alert>;
    }

    if (!batch) {
        return <Alert severity="warning">Batch introuvable.</Alert>;
    }

    return (
        <Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                Batch ID : {batch.batchId}
            </Typography>

            <ScoringBatchStatusCard
                batch={batch}
                onRetry={handleRetry}
                retryLoading={retryLoading}
            />

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                Résultats ({batch.total})
            </Typography>

            <ScoringResultsTable results={batch.results} loading={loading} />
        </Box>
    );
};

export default ScoringBatchDetailModule;
